import { useCallback, useEffect, useState } from 'react';
import { useSubscription } from './useSubscription';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export interface LimitCheckResult {
  allowed: boolean;
  current: number;
  limit: number | null;
  isPremiumFeature: boolean;
}

export const usePlanLimits = () => {
  const { user, role } = useAuth();
  const { plan, isPaid, loading: subLoading } = useSubscription();

  // ── Lightweight count-only state ───────────────────────────────────────────
  // Replace full useAccounts/useCards/useGoals/usePayees hooks with minimal
  // count queries. This eliminates 4 redundant full-data fetches on pages that
  // already have those hooks loaded independently.
  const [accountCount, setAccountCount]         = useState(0);
  const [cardCount,    setCardCount]             = useState(0);
  const [goalCount,    setGoalCount]             = useState(0);
  const [activePayeeCount, setActivePayeeCount]  = useState(0);
  const [countsReady,  setCountsReady]           = useState(false);

  useEffect(() => {
    if (!user) {
      setAccountCount(0); setCardCount(0); setGoalCount(0); setActivePayeeCount(0);
      setCountsReady(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      supabase.from('accounts').select('id', { count: 'exact', head: true })
        .eq('user_id', user.id),
      supabase.from('cards').select('id', { count: 'exact', head: true })
        .eq('user_id', user.id).eq('is_archived', false),
      supabase.from('goals').select('id', { count: 'exact', head: true })
        .eq('user_id', user.id),
      supabase.from('payees').select('id', { count: 'exact', head: true })
        .eq('user_id', user.id).eq('is_active', true),
    ]).then(([accRes, cardRes, goalRes, payeeRes]) => {
      if (cancelled) return;
      setAccountCount(accRes.count ?? 0);
      setCardCount(cardRes.count ?? 0);
      setGoalCount(goalRes.count ?? 0);
      setActivePayeeCount(payeeRes.count ?? 0);
      setCountsReady(true);
    });
    return () => { cancelled = true; };
  }, [user]);

  const ADMIN_ROLES = ['master_admin', 'admin', 'support', 'finance'];
  const isAdmin = !!role && ADMIN_ROLES.includes(role);

  // -1 = unlimited in the plans table
  const isUnlimited = (val: number | null | undefined) => val === -1 || val === null;

  const checkAccountLimit = useCallback((): LimitCheckResult => {
    if (subLoading || !countsReady) return { allowed: true, current: accountCount, limit: null, isPremiumFeature: false };
    if (isAdmin) return { allowed: true, current: accountCount, limit: null, isPremiumFeature: false };
    const limit = plan?.accounts_limit ?? 1;
    const unlimited = isUnlimited(limit);
    return {
      allowed: unlimited || accountCount < limit,
      current: accountCount,
      limit: unlimited ? null : limit,
      isPremiumFeature: false,
    };
  }, [plan, accountCount, isAdmin, subLoading, countsReady]);

  const checkCardLimit = useCallback((): LimitCheckResult => {
    if (subLoading || !countsReady) return { allowed: true, current: cardCount, limit: null, isPremiumFeature: false };
    if (isAdmin) return { allowed: true, current: cardCount, limit: null, isPremiumFeature: false };
    const limit = plan?.cards_limit ?? 1;
    const unlimited = isUnlimited(limit);
    return {
      allowed: unlimited || cardCount < limit,
      current: cardCount,
      limit: unlimited ? null : limit,
      isPremiumFeature: false,
    };
  }, [plan, cardCount, isAdmin, subLoading, countsReady]);

  const checkGoalLimit = useCallback((): LimitCheckResult => {
    if (subLoading || !countsReady) return { allowed: true, current: goalCount, limit: null, isPremiumFeature: false };
    if (isAdmin) return { allowed: true, current: goalCount, limit: null, isPremiumFeature: false };
    const limit = plan?.goals_limit ?? 2;
    const unlimited = isUnlimited(limit);
    return {
      allowed: unlimited || goalCount < limit,
      current: goalCount,
      limit: unlimited ? null : limit,
      isPremiumFeature: false,
    };
  }, [plan, goalCount, isAdmin, subLoading, countsReady]);

  const checkTransactionLimit = useCallback(async (): Promise<LimitCheckResult> => {
    // Guard: never block while subscription is loading
    if (subLoading) return { allowed: true, current: 0, limit: null, isPremiumFeature: false };
    if (isAdmin) return { allowed: true, current: 0, limit: null, isPremiumFeature: false };
    const monthlyLimit = plan?.monthly_transactions_limit ?? 30;
    const unlimited = isUnlimited(monthlyLimit);

    if (unlimited) {
      return { allowed: true, current: 0, limit: null, isPremiumFeature: false };
    }
    if (!user) return { allowed: false, current: 0, limit: monthlyLimit, isPremiumFeature: false };

    // ── Try server-side validation first (authoritative, uses service role) ──
    try {
      const { data: serverResult, error: fnErr } = await supabase.functions.invoke(
        'validate-transaction-limit',
        { method: 'POST' },
      );
      if (!fnErr && serverResult) {
        return {
          allowed: serverResult.allowed,
          current: serverResult.current ?? 0,
          limit: serverResult.limit ?? null,
          isPremiumFeature: false,
        };
      }
    } catch {
      // Fall through to local DB query if edge function is unavailable
    }

    // ── Fallback: direct DB query (same logic as before) ─────────────────────
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

    const { count } = await supabase
      .from('transactions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .gte('date', startOfMonth)
      .lte('date', endOfMonth)
      .is('deleted_at', null);

    const current = count ?? 0;
    return {
      allowed: current < monthlyLimit,
      current,
      limit: monthlyLimit,
      isPremiumFeature: false,
    };
  }, [plan, user, isAdmin, subLoading]);

  const checkPayeeLimit = useCallback((): LimitCheckResult => {
    if (subLoading || !countsReady) return { allowed: true, current: activePayeeCount, limit: null, isPremiumFeature: false };
    if (isAdmin) return { allowed: true, current: activePayeeCount, limit: null, isPremiumFeature: false };
    const raw = plan?.payees_limit;
    const unlimited = raw === -1 || raw === null || raw === undefined;
    const limit = unlimited ? null : (raw as number);
    return {
      allowed: unlimited || activePayeeCount < (limit as number),
      current: activePayeeCount,
      limit,
      isPremiumFeature: false,
    };
  }, [plan, activePayeeCount, isAdmin, subLoading, countsReady]);

  const hasReportsAccess = isAdmin ? true : (plan?.reports_enabled ?? false);

  const getTransactionCount = useCallback(async (): Promise<number> => {
    if (!user || subLoading) return 0;
    // If unlimited, skip the DB count — saves a round-trip
    if (isAdmin || isUnlimited(plan?.monthly_transactions_limit)) return 0;
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    const { count } = await supabase
      .from('transactions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .gte('date', startOfMonth)
      .lte('date', endOfMonth)
      .is('deleted_at', null);
    return count ?? 0;
  }, [user, plan, isAdmin, subLoading]);

  return {
    plan,
    isPaid: isAdmin ? true : isPaid,
    subLoading,
    checkAccountLimit,
    checkCardLimit,
    checkGoalLimit,
    checkTransactionLimit,
    checkPayeeLimit,
    getTransactionCount,
    hasReportsAccess,
    /**
     * null = unlimited (never show limit UI)
     * number = real monthly limit
     */
    monthlyTransactionLimit: subLoading
      ? null  // While loading: treat as unlimited to avoid flash of limit UI
      : isAdmin
        ? null
        : isUnlimited(plan?.monthly_transactions_limit)
          ? null
          : (plan?.monthly_transactions_limit ?? 30),
  };
};
